import React, { useState, useEffect } from 'react';
import { websitesAPI } from '../api/websites';
import './VersionHistory.css';

function VersionHistory({ websiteId, audienceId, isOpen, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (isOpen && websiteId && audienceId) {
      loadVersions();
    }
  }, [isOpen, websiteId, audienceId]);

  const loadVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await websitesAPI.getVersions(websiteId, audienceId);
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (version) => {
    if (!window.confirm(`Are you sure you want to restore version from ${formatDate(version.savedAt)}? This will overwrite the current content.`)) {
      return;
    }

    setRestoring(version.version);
    try {
      await websitesAPI.restoreVersion(websiteId, audienceId, version.version);
      // Reload versions to show the restored version
      await loadVersions();
      // Notify parent to reload content
      if (onRestore) {
        onRestore();
      }
      alert('Version restored successfully! Please reload the editor content.');
    } catch (err) {
      console.error('Failed to restore version:', err);
      alert(`Failed to restore version: ${err.message}`);
    } finally {
      setRestoring(null);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatVersion = (versionString) => {
    // Convert "2024-01-15-14-30-45" to "Jan 15, 2024 2:30 PM"
    try {
      const parts = versionString.split('-');
      if (parts.length >= 6) {
        const date = new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2]),
          parseInt(parts[3]),
          parseInt(parts[4]),
          parseInt(parts[5])
        );
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return versionString;
    } catch (e) {
      return versionString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="version-history-sidebar">
      <div className="version-history-header">
        <h3>Version History</h3>
        <button className="version-history-close" onClick={onClose} title="Close">
          ×
        </button>
      </div>
      
      <div className="version-history-content">
        {loading ? (
          <div className="version-history-loading">Loading versions...</div>
        ) : error ? (
          <div className="version-history-error">Error: {error}</div>
        ) : versions.length === 0 ? (
          <div className="version-history-empty">No versions found</div>
        ) : (
          <div className="version-history-list">
            {versions.map((version, index) => (
              <div key={version.version} className="version-item">
                <div className="version-item-header">
                  <div className="version-item-info">
                    <div className="version-item-title">
                      {index === 0 ? 'Current' : `Version ${versions.length - index}`}
                    </div>
                    <div className="version-item-date">
                      {formatDate(version.savedAt)}
                    </div>
                    <div className="version-item-id">
                      {formatVersion(version.version)}
                    </div>
                  </div>
                </div>
                <div className="version-item-actions">
                  {version.htmlUrl && (
                    <a
                      href={version.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="version-item-preview"
                      title="Preview in new tab"
                    >
                      Preview
                    </a>
                  )}
                  {index > 0 && (
                    <button
                      className="version-item-restore"
                      onClick={() => handleRestore(version)}
                      disabled={restoring === version.version}
                      title="Restore this version"
                    >
                      {restoring === version.version ? 'Restoring...' : 'Restore'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VersionHistory;

